// Shared post-formatting helpers. Kept in one place so a card, an article and
// the JSON-LD never disagree about a post's excerpt, date or reading time.

/** Strip HTML tags and collapse whitespace. */
export function plainText(html = '') {
  return String(html)
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Truncate on a word boundary so excerpts never cut mid-word. */
export function truncate(text = '', max = 160) {
  const s = String(text).trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Prefer an author-written excerpt (better for search snippets and social
 * cards); fall back to the opening of the body.
 */
export function postExcerpt(post, max = 160) {
  if (!post) return '';
  return truncate(post.excerpt || plainText(post.content), max);
}

/** Meta-description length: Google truncates around 155–160 characters. */
export const metaDescription = (post) => postExcerpt(post, 155);

const WORDS_PER_MINUTE = 220;

export function wordCount(post) {
  if (!post) return 0;
  const text = plainText(post.content);
  return text ? text.split(' ').length : 0;
}

export function readingMinutes(post) {
  return Math.max(1, Math.round(wordCount(post) / WORDS_PER_MINUTE));
}

export const publishedAt = (post) => post?.createdOn || post?.createdAt || null;
export const modifiedAt = (post) => post?.updatedOn || post?.updatedAt || publishedAt(post);

/** Machine-readable date for <time datetime> and JSON-LD. */
export function isoDate(value) {
  if (!value) return undefined;
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Human-readable date. Fixed locale so server-prerendered and client-rendered
 *  HTML match — a mismatch causes a visible hydration flash. */
export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).split('T')[0];
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
